// ============================================================
// PERRY — PROGRESSION (the Firebase layer)
// The ONLY file that touches the database. Every path derives
// from ONE BASE constant (Firebase Law #2 — one drawer, under
// the games/ umbrella of the shared terror-tcg project).
//
// THE LAWS HONORED HERE:
//   • One project (terror-tcg). One drawer (games/perry/...).
//   • Currency: Perry MINTS ITS OWN LANE — ⋔ Kindling, at
//     profiles/{uid}/kindling (2026-07-28 ruling, IE ECONOMY LAW
//     v1 §6). It supersedes the 2026-07-24 "mint nothing" ruling.
//     Perry writes kindling. Perry writes nobody else's currency.
//     ◆ divineCrests is the Empire's shared reserve: Perry may
//     GRANT it from one-time sources only (achievements) and SPEND
//     it on the top shelf only. Both lanes are DELTA TRANSACTIONS
//     ONLY. Reads of any currency are universal.
//   • Narrow listeners — NEVER the profile root. We listen to
//     profiles/{uid}/kindling, profiles/{uid}/divineCrests and
//     games/perry/{uid}/soul — leaves only (the Annihilation
//     freeze-then-catch-up scar).
//   • Append-only for anything earned: achievements ride the
//     shared engine; records only ever grow; nodes/milestones
//     are merge-writes, never overwrites.
//   • Grant-before-flag on every reward claim.
//   • auth.uid is the only identity. The ?uid= param is a HINT.
//   • No new profile-level fields — zero whitelist exposure.
//
// THE ONE INTENTIONAL WIPE: forgeSoul() (New Game). The Soul is
// lost all at once — that is Law 4 of the Empire, by design, and
// it is the ONLY full-object set() in this file. Records, Crests
// and Achievements survive a New Game; only the Soul burns.
// ============================================================

import { ref, get, set, update, onValue, runTransaction } from "firebase/database";
import { db } from "./firebase";
import { freshSoul, RULES, spentPoints } from "./perryData";

// ── THE BASE — one drawer, one truth ─────────────────────────
export const BASE = "games/perry";
const soulRef = (uid) => ref(db, `${BASE}/${uid}/soul`);
const recordsRef = (uid) => ref(db, `${BASE}/${uid}/records`);
const crestsRef   = (uid) => ref(db, `profiles/${uid}/divineCrests`);
const kindlingRef = (uid) => ref(db, `profiles/${uid}/kindling`);

// ── THE SOUL ─────────────────────────────────────────────────

export const getSoul = async (uid) => {
  if (!uid) return null;
  const snap = await get(soulRef(uid));
  return snap.exists() ? snap.val() : null;
};

// Narrow leaf listener. Returns unsubscribe.
export const listenToSoul = (uid, cb) => {
  if (!uid) { cb(null); return () => {}; }
  return onValue(soulRef(uid), (snap) => cb(snap.exists() ? snap.val() : null));
};

// NEW GAME — the one intentional wipe. The Soul is lost all at
// once; everything shared survives. Callers confirm with the
// player FIRST (the two-step DELETE confirm is roadmapped).
export const forgeSoul = async (uid, species) => {
  if (!uid || !species) return null;
  const soul = freshSoul(species);
  await set(soulRef(uid), soul);
  return soul;
};

// ══════════════════════════════════════════════════════════════
//  ASCENSIONS — points BOUGHT, add-only, never decremented
// ══════════════════════════════════════════════════════════════
// The Two-Currency Law (2026-07-29) retired Renown. Ascension used
// to be derived free from an unspendable standing pool; it is now
// PURCHASED with ⋔ Kindling at RULES.ASCEND_COST apiece, because a
// coin that buys both an axe and a star is the whole reason caP'n
// merged them — and a choice you are not charged for is not one.
//
// `ascensions` is the count ever bought. It goes UP and it is the
// only thing here that is stored; what is FREE right now is still
// derived, as (bought − spent on nodes). Law 14 intact.
//
// SPEND FIRST, GRANT SECOND — the reverse of grant-before-flag,
// and deliberately so. This is not a reward, it is a PURCHASE: a
// crash between the two costs the player a point they paid for
// (recoverable, and visible — they can see the ⋔ went), whereas
// granting first would hand out free points to anyone who could
// close a tab at the right moment. Rewards fail generous; sales
// fail honest.
export const buyAscension = async (uid, amount = 1) => {
  if (!uid || amount <= 0) return false;
  const cost = RULES.ASCEND_COST * amount;
  const paid = await spendKindling(uid, cost);
  if (!paid) return false;
  await runTransaction(ref(db, `${BASE}/${uid}/soul/ascensions`), (cur) =>
    (cur || 0) + amount
  );
  return true;
};

// ══════════════════════════════════════════════════════════════
//  THE RENOWN MIGRATION — nobody logs in to a nerf
// ══════════════════════════════════════════════════════════════
// A Soul forged before 2026-07-29 carries `renown`, and possibly a
// sky full of stars it paid for with renown that no longer exists.
// This runs once, on load, and it is DELIBERATELY GENEROUS in both
// directions at the same time:
//
//   1. Every star already taken is PAID FOR — `ascensions` is
//      floored at `spentPoints(soul)`. Nobody wakes up owing points
//      for a climb they already made.
//   2. The whole renown balance converts to ⋔ at 1:1 on top of
//      that. Unspent standing becomes spendable coin.
//
// Yes, that pays twice for renown that had been spent on stars.
// That is the intended reading of "nothing earned is ever taken
// back" — the alternative is arithmetic that leaves somebody worse
// off than they went to bed, and no amount of ledger tidiness is
// worth that. It fires ONCE per account, flagged, and there is no
// second cohort it can ever pay again.
export const migrateRenown = async (uid, soul) => {
  if (!uid) return 0;
  const flagRef = ref(db, `${BASE}/${uid}/migrations/oneCoinV1`);
  const flag = await get(flagRef);
  if (flag.exists()) return 0;

  let moved = 0;
  const res = await runTransaction(ref(db, `${BASE}/${uid}/soul/renown`), (cur) => {
    moved = cur || 0;
    return null;   // the field leaves the Soul entirely
  });
  if (!res.committed) return 0;

  const owed = spentPoints(soul);
  await runTransaction(ref(db, `${BASE}/${uid}/soul/ascensions`), (cur) =>
    Math.max(cur || 0, owed)
  );
  if (moved > 0) await awardKindling(uid, moved);
  await set(flagRef, { at: Date.now(), renown: moved, ascensions: owed });
  return moved;
};

// Ascend — take a constellation node. Merge-write, never overwrite.
// (Legality — cost, prereqs, available points — is checked by the
//  Customize screen against derived values; this is the write.)
export const takeNode = async (uid, node) => {
  if (!uid || !node) return;
  const patch = { [`nodes/${node.id}`]: true };
  if (node.deviant) patch.deviant = true; // THE FORK — permanent, flagged
  await update(soulRef(uid), patch);
};

// The Growth Law's permanent record. Caller guards deviance.
export const growChao = async (uid) => {
  if (!uid) return;
  await runTransaction(ref(db, `${BASE}/${uid}/soul/chaoGrown`), (cur) =>
    Math.min(RULES.CHAO_NAT_CAP, (cur || 0) + 1)
  );
};

// Milestones — merge-write flags (the reward is granted BEFORE
// the flag by the calling sequence — grant-before-flag).
export const grantMilestone = async (uid, key) => {
  if (!uid || !key) return;
  await update(soulRef(uid), { [`milestones/${key}`]: true });
};

// Arm/own a Tome (count up), or a Curio (flag). Merge-writes.
export const grantTome = async (uid, tomeId) => {
  if (!uid || !tomeId) return;
  await runTransaction(ref(db, `${BASE}/${uid}/soul/tomes/${tomeId}`), (cur) => (cur || 0) + 1);
};
export const consumeTome = async (uid, tomeId) => {
  if (!uid || !tomeId) return;
  await runTransaction(ref(db, `${BASE}/${uid}/soul/tomes/${tomeId}`), (cur) =>
    Math.max(0, (cur || 0) - 1)
  );
};
// ── TREASURES — equipment. Own is a merge-flag; equip is a leaf.
// Neither is ever a full-object set(): a Treasure you bought is
// earned data, and earned data is only ever added to.
export const grantTreasure = async (uid, treasureId) => {
  if (!uid || !treasureId) return;
  await update(soulRef(uid), { [`treasures/${treasureId}`]: true });
};

// Equipping is a preference, not a reward — a single leaf write.
export const equipTreasure = async (uid, treasureId) => {
  if (!uid || !treasureId) return;
  await update(soulRef(uid), { treasure: treasureId });
};

// ══════════════════════════════════════════════════════════════
//  THE SHEATHE'S LEDGER — Ascending a weapon (2026-07-30)
// ══════════════════════════════════════════════════════════════
// Everything a weapon becomes is written under games/perry/{uid}/
// soul/arms/{treasureId}/, which is inside the drawer this file
// has always owned. NO NEW PROFILE FIELD, no whitelist exposure,
// no rules change — the arms map rides the Soul exactly like
// `nodes` and `milestones` already do, and it burns on New Game
// with everything else, which is correct: Law 4 takes the Soul,
// and what your sword became is unambiguously part of your Soul.
//
// Every write below is a MERGE or a leaf transaction. There is no
// full-object set() anywhere in this section, because a weapon's
// rung map is earned data and earned data is only ever added to.

// ── BUY A RUNG — spend first, grant second ──────────────────
// The deliberate inversion of grant-before-flag, and the same
// reasoning `buyAscension` is built on: this is a PURCHASE, not a
// reward. A crash between the two costs the player a rung they
// paid for — recoverable, visible, and their ⋔ is provably gone.
// Granting first would hand out free rungs to anybody who could
// close a tab at the right moment. Rewards fail generous; sales
// fail honest.
//
// Legality — does he own the weapon, is the previous rung lit, can
// he afford it — is checked by the Ascension screen against the
// derived values in `armProgress`. This is the write.
export const buyArmRung = async (uid, treasureId, rungKey, cost) => {
  if (!uid || !treasureId || !rungKey) return false;
  if (cost > 0) {
    const paid = await spendKindling(uid, cost);
    if (!paid) return false;
  }
  await update(soulRef(uid), { [`arms/${treasureId}/rungs/${rungKey}`]: true });
  return true;
};

// ── REFORGE — move the Sheathe's attention to another weapon ──
// caP'n: "can switch and level up others if you want, pay currency
// and reset your levels."
//
// NOTE WHAT THIS FUNCTION DOES NOT DO: it does not clear anything.
// The rungs on the weapon you are leaving stay exactly where they
// are and are still lit when you come back. "Reset your levels" is
// what the PLAYER experiences — his effective stats drop to the
// new weapon's, honestly, in the open — and charging for it makes
// the choice weigh something. Deleting his investment would make
// the fee a punishment instead of a price, and it would be the one
// destructive write in a file that does not have any.
export const reforgeTo = async (uid, treasureId, cost) => {
  if (!uid || !treasureId) return false;
  if (cost > 0) {
    const paid = await spendKindling(uid, cost);
    if (!paid) return false;
  }
  await update(soulRef(uid), { treasure: treasureId });
  return true;
};

// ── THE AWAKENING — begin the mission ───────────────────────
// caP'n: "you start a mission, defeat certain amount of enemies
// with it even now (stat wise, it's back to level 1 not visually)
// to Awaken it and now it's Lord form unlocks all the cool stuff."
//
// Writing this object is what STRIPS the weapon — `deriveArm`
// returns the base row while an unfinished `awakening` exists, so
// this one merge-write is the entire mechanism. Nothing else in
// the codebase needs to know the mission is running.
//
// It refuses to restart an open mission. Re-entering the screen
// mid-Awakening must never quietly reset your count to zero.
export const beginAwakening = async (uid, treasureId) => {
  if (!uid || !treasureId) return false;
  const cur = await get(ref(db, `${BASE}/${uid}/soul/arms/${treasureId}`));
  const st = cur.exists() ? cur.val() : null;
  if (st?.lord) return false;              // already past it
  if (st?.awakening) return true;          // already walking it — leave it alone
  await update(soulRef(uid), {
    [`arms/${treasureId}/awakening`]: { at: Date.now(), ruins: 0 },
  });
  return true;
};

// Progress — one transaction on the leaf, add-only, so two runs
// settling at once cannot lose a Ruin between them.
export const awakenProgress = async (uid, treasureId, n = 1) => {
  if (!uid || !treasureId || n <= 0) return;
  await runTransaction(
    ref(db, `${BASE}/${uid}/soul/arms/${treasureId}/awakening/ruins`),
    (cur) => (cur || 0) + n
  );
};

// ── LORD — grant before flag ────────────────────────────────
// A REWARD, so the order flips back the right way round: the crown
// goes on FIRST, and only then does the mission close. A crash
// between them leaves a Lord weapon with a stale open mission,
// which the Ascension screen reads as "finished, tidy this up" —
// annoying. The reverse would close the mission on a weapon that
// never got crowned, which is theft.
export const crownArm = async (uid, treasureId) => {
  if (!uid || !treasureId) return;
  await update(soulRef(uid), { [`arms/${treasureId}/lord`]: true });
  await update(soulRef(uid), { [`arms/${treasureId}/awakening`]: null });
};


// ── THE GRIMOIRE — which five you carry ──────────────────────
// A PREFERENCE, not a reward, so it is a single leaf write on a
// path nobody else touches — exactly like equipTreasure and
// setFinish, and for the same reason. It is also the one place in
// this file that writes an ARRAY, which is worth saying out loud:
// Realtime Database stores an array as an object with integer
// keys, so a slot set to null vanishes and the indices after it
// shift down. That would silently re-key a player's whole loadout.
//
// So nulls go to the wire as the string "-", and deriveSlots
// treats anything it does not recognise as empty. Ugly on the
// wire, correct on the way back, and it never re-keys anybody.
export const setSlots = async (uid, slots) => {
  if (!uid || !Array.isArray(slots)) return;
  await update(soulRef(uid), { slots: slots.map((s) => s || "-") });
};

// Cosmetic only. Costs nothing, unlocks nothing, breaks nothing.
export const setFinish = async (uid, finishId) => {
  if (!uid || !finishId) return;
  await update(soulRef(uid), { finish: finishId });
};

export const grantCurio = async (uid, curioId) => {
  if (!uid || !curioId) return;
  await update(soulRef(uid), { [`curios/${curioId}`]: true });
};
export const setBloodDebt = async (uid, amount) => {
  if (!uid) return;
  await update(soulRef(uid), { bloodDebt: amount });
};

// ══════════════════════════════════════════════════════════════
//  TWO LANES, ONE RULE: DELTA, NEVER READ-MODIFY-WRITE
// ══════════════════════════════════════════════════════════════
// Both helpers below transact on the LEAF — the single number at
// the end of the path — never on the profile object. This is the
// April 2026 lesson, written in code: a full-object set() on a
// profile wiped holo cards and crests when two writes landed at
// once. Add-only on a leaf cannot do that, by construction.
//
// ── ⋔ KINDLING — Perry's own lane ─────────────────────────────
// Repeatable. Earned per answer, per chain, per arrival. Perry is
// the only app in the Empire that writes this path.

export const awardKindling = async (uid, amount) => {
  if (!uid || !amount || amount <= 0) return;
  await runTransaction(kindlingRef(uid), (cur) => (cur || 0) + amount);
};

// Spend — transactional, aborts if short. Returns true on success.
export const spendKindling = async (uid, amount) => {
  if (!uid || !amount || amount <= 0) return false;
  const res = await runTransaction(kindlingRef(uid), (cur) => {
    const c = cur || 0;
    if (c < amount) return; // abort — insufficient
    return c - amount;
  });
  return !!res.committed;
};

export const listenToKindling = (uid, cb) => {
  if (!uid) { cb(0); return () => {}; }
  return onValue(kindlingRef(uid), (snap) => cb(snap.val() || 0));
};

// ── ◆ DIVINE CRESTS — the Empire's reserve, not ours ──────────
// FAUCET LAW: awardCrests may ONLY be called from a one-time,
// idempotent source. Today that means achievements and nothing
// else. If you are about to call it from anywhere a player can
// reach twice — a parry, a run, a login, a score — you want
// awardKindling instead, every time, no exceptions worth having.
//
// NOTE FOR THE PIERRE HANDOFF: when Pierre ships the shared
// grantCrests() helper, this function is deleted and that one is
// imported. It is deliberately written in the canonical shape so
// the swap is one import line and zero call-site edits. Do not
// "improve" it in the meantime — a reimplementation that drifts
// from the canonical shape is the thing the Lane Law forbids.

export const awardCrests = async (uid, amount) => {
  if (!uid || !amount || amount <= 0) return;
  await runTransaction(crestsRef(uid), (cur) => (cur || 0) + amount);
};

// Spend — top shelf only. See CURRENCIES / the Clapper.
export const spendCrests = async (uid, amount) => {
  if (!uid || !amount || amount <= 0) return false;
  const res = await runTransaction(crestsRef(uid), (cur) => {
    const c = cur || 0;
    if (c < amount) return; // abort — insufficient
    return c - amount;
  });
  return !!res.committed;
};

export const listenToCrests = (uid, cb) => {
  if (!uid) { cb(0); return () => {}; }
  return onValue(crestsRef(uid), (snap) => cb(snap.val() || 0));
};

// ── THE ONE-TIME MIGRATION — ◆ carried across to ⋔ ────────────
// caP'n's ruling, 2026-07-28: convert, 1:1. Every ◆ a player holds
// on the day of the split was earned under the old repeatable
// rules, spending on a shop that is now priced in ⋔. Leaving it
// stranded in the reserve lane would have taken a shop away from
// people who paid for it.
//
// Runs ONCE per account, flagged in OUR drawer, never in profiles/
// — no new profile field, no whitelist exposure. Idempotent by the
// flag AND by the transaction: a crash mid-way costs a re-run that
// finds a zero balance and does nothing.
//
// ORDER IS DELIBERATE and it is the opposite of grant-before-flag,
// because this is a MOVE, not a grant: we take the ◆ first (a
// transaction that returns exactly what it removed), then add that
// much ⋔, then set the flag. A crash after the debit and before
// the credit is the one bad window, and it is one transaction
// wide. The reverse order could double the player's money on every
// retry, which is the worse failure by far.
export const migrateCrestsToKindling = async (uid) => {
  if (!uid) return 0;
  const flagRef = ref(db, `${BASE}/${uid}/migrations/kindlingV1`);
  const flag = await get(flagRef);
  if (flag.exists()) return 0;

  let moved = 0;
  const res = await runTransaction(crestsRef(uid), (cur) => {
    moved = cur || 0;
    return 0;
  });
  if (!res.committed) return 0;

  if (moved > 0) await awardKindling(uid, moved);
  await set(flagRef, { at: Date.now(), moved });
  return moved;
};

// ── THE LEDGER — what this ACCOUNT has ever bought ────────────
// Separate from the Soul on purpose. The Soul burns on New Game
// (Law 4); the ledger does not, because it is a record of money
// that left, and money that left is history. Records and Crests
// already survive a New Game — this is the same shelf.
//
// It exists for `oncePerAccount` items (the ◆ top shelf). Under
// the Mythical Law a Crest-bought thing is one per account, and
// "account" has to mean something a New Game cannot launder.
//
// THE KIND HALF: an account that has already paid gets the thing
// handed back for free after a New Game. You do not re-buy what
// you bought. Whyllie keeps books, and Whyllie is a lot of things
// but he is not a thief.
export const recordPurchase = async (uid, id) => {
  if (!uid || !id) return;
  await update(ref(db, `${BASE}/${uid}/ledger`), { [id]: Date.now() });
};

export const hasPurchased = async (uid, id) => {
  if (!uid || !id) return false;
  const snap = await get(ref(db, `${BASE}/${uid}/ledger/${id}`));
  return snap.exists();
};

// ── RECORDS — only ever grow ─────────────────────────────────
// One transaction merges a run's deltas: counters add, bests max,
// seen-map unions. Records survive New Game — history is history.
export const recordRun = async (uid, r) => {
  if (!uid || !r) return;
  await runTransaction(recordsRef(uid), (cur) => {
    const c = cur || {};
    const seen = { ...(c.seen || {}) };
    for (const id of r.seen || []) seen[id] = true;
    const clears = { ...(c.clears || {}) };
    if (r.clearedAs) clears[r.clearedAs] = true; // per-species Act I clears
    return {
      runs: (c.runs || 0) + 1,
      falls: (c.falls || 0) + (r.fell ? 1 : 0),
      arrivals: (c.arrivals || 0) + (r.arrived ? 1 : 0),
      parries: (c.parries || 0) + (r.parries || 0),
      perfects: (c.perfects || 0) + (r.perfects || 0),
      ruins: (c.ruins || 0) + (r.ruins || 0),
      bestChain: Math.max(c.bestChain || 0, r.bestChain || 0),
      seen,
      clears,
    };
  });
};

export const getRecords = async (uid) => {
  if (!uid) return null;
  const snap = await get(recordsRef(uid));
  return snap.exists() ? snap.val() : null;
};

// ── CROSS-APP HANDOFF — the ?uid= HINT ───────────────────────
// Reads the param, strips it from the URL immediately, and hands
// it back as an EXPECTATION for the login screen. It is never an
// identity — the arriving player still authenticates for real,
// and every rule compares against auth.uid.
export const readUidHint = () => {
  try {
    const url = new URL(window.location.href);
    const hint = url.searchParams.get("uid");
    if (hint) {
      url.searchParams.delete("uid");
      window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    }
    return hint || null;
  } catch (_) {
    return null;
  }
};

// Outbound handoff → Unstable Pierre (the hub). Aborts w/ warning
// if unauthenticated — never sends an empty hint.
export const goToUnstablePierre = (uid) => {
  if (!uid) {
    console.warn("[perry] No authenticated uid — refusing outbound handoff.");
    return false;
  }
  window.location.href = `https://unstable-pierre.pages.dev?uid=${encodeURIComponent(uid)}`;
  return true;
};
